/*!
// <copyright file="PerformanceTimer.js" company="Microsoft">
//     Copyright (c) Microsoft Corporation.  All rights reserved.
// </copyright>
*/

(function () {
    'use strict';

    if (!$dyn.flags.disableTimer) {
        $dyn.ui.PerformanceTimer = {
            // The performance timer tracks the time it takes to do a set of operations on the client.

            // *Terms*
            // Client operation time:  the client is doing a long running operation.  Could be blocking or non-blocking.
            // Note: $dyn.clientBusy is used to represent this time.  The test architectures can also use this flag to determine if they can get the UI state. 
            // Client blocking time: the client is doing an operation blocking user interactions.  
            // Ajax/server time: the client is doing a server request
            // Note: $dyn.ui.PerformanceTimer.startAjax()/$dyn.ui.PerformanceTimer.stopAjax() can be used to time server operations.  
            // This should be done as close the request stop/end as possible.  
            // Due to the jQuery ajax API, server time will include serialization/deserialization of the request and thus may be longer 
            // than the network time as seen a network profiler
            // The performance timer does not add any overhead when not enabled.  It is loaded on demand and its apis are only available when loaded.  
            // The performance timer use the debug resources api to load its resources ($dyn.debugSettings[settingName].resources)
            // The performance timer has a minor impact to the initial loading due to loading the static resources for the performance timer.  
            // Loading of these file will come from the browser's cache after the first load.

            duration: $dyn.observable(),
            trivialTime: 70,//ms
            expectedTime: $dyn.observable(),
            ajaxTime: $dyn.observable(0),
            pendingBlockingDivRequests: 0,
            blockingDivStart: 0,
            blockingDivEnd: 0,
            blockingDivTime: 0,
            blockingDivDescription: '',
            interactionType: $dyn.observable(''),
            pendingAjaxRequests: 0,
            bandwidth: $dyn.observable(0),
            indicator: $dyn.observable(),
            startTime: undefined,
            actionSet: {},
            log: $dyn.log.performance,
            actions: $dyn.observable([]),
            action: '',
            timings: $dyn.observable([]),
            segments: [],
            steps: $dyn.observable([]),
            renderTime: $dyn.computed(function () {
                var self = $dyn.ui.PerformanceTimer;
                return (self.duration() - self.ajaxTime());
            }),
            oppTiming: $dyn.observable(),
            
            // action types:
            clientType: 0,
            serverType: 1,

            _startAjax: function (activityId) {
                var self = $dyn.ui.PerformanceTimer;
                if (self.pendingAjaxRequests == 0) {
                    if (self.inClient) {
                        self.clientEnd = performance.now();
                        $dyn.timeEnd('client');
                        self.inClient = false;
                        self.segments.push({
                            Name: $dyn.label('PerformanceTimer_ClientAction'),
                            Duration: self.clientEnd - self.clientStart,
                            StartTime: self.clientStart,
                            Type: self.clientType
                        });
                    }

                    self.ajaxStart = performance.now();

                    if (!self.startTime) {
                        self.startTime = self.ajaxStart;
                    }

                    if (!self.ajaxEnd) {
                        self.ajaxEnd = self.ajaxStart;
                    }

                    if (!self.ajaxActivityId) {
                        self.ajaxActivityId = activityId;
                    }

                    $dyn.time('ajax');
                    self.log && self.log('performance: ajax started: duration since last end: %s, overall: %s', self.ajaxStart - self.ajaxEnd, self.ajaxStart - self.startTime);
                } else {
                    self.log && self.log('performance: ajax started with pending ajax requests: %s', self.pendingAjaxRequests);
                }
                self.pendingAjaxRequests++;
            },
            _stopAjax: function () {
                var self = $dyn.ui.PerformanceTimer;

                self.pendingAjaxRequests--;
                if (self.pendingAjaxRequests == 0) {
                    self.clientStart = self.ajaxEnd = performance.now();
                    var start = self.ajaxStart;
                    var time = self.ajaxEnd - start;
                    var total = self.ajaxTime() + time;
                    self.ajaxTime(total);
                    $dyn.timeEnd('ajax');
                    self.log && self.log('performance: ajax completed: %s, ajax total: %s, overall duration %s', time, total, self.ajaxEnd - self.startTime);
                    self.ajaxStart = null;
                    self.lastAjaxSegment = {
                        Name: $dyn.label('PerformanceTimer_ServerAction'),
                        Duration: time,
                        StartTime: start,
                        Type: self.serverType
                    };
                    self.segments.push(self.lastAjaxSegment);
                    self.inClient = true;
                    $dyn.time('client');
                } else {
                    self.log && self.log('performance: ajax completed with pending ajax requests: %s', self.pendingAjaxRequests);
                }
            },
            _ajaxResponse: function (request) {
                var self = $dyn.ui.PerformanceTimer;

                if (request && request.responseText) {
                    var length = request.responseText.length;
                    var total = self.bandwidth() + request.responseText.length;
                    self.bandwidth(total);
                    self.log && self.log('performance: ajax content length: %s, total: %s', length, total);
                }
            },

            /**
             * @memberof $dyn.ui.PerformanceTimer
             * @function _startBlockingDiv
             * @internal
             * @summary Log the start of a blocking div
             * @param {string} blockingDivDescription
             */
            _startBlockingDiv: function (blockingDivDescription) {
                var self = $dyn.ui.PerformanceTimer;

                if (self.pendingBlockingDivRequests == 0) {
                    self.blockingDivStart = performance.now();
                    self.blockingDivTime = 0;
                    self.blockingDivDescription = blockingDivDescription;

                    self.log && self.log('performance: blockingDiv %s started: duration since last end: %s', blockingDivDescription, self.blockingDivStart - self.blockingDivEnd);
                } else {
                    self.blockingDivDescription += ", " + blockingDivDescription;
                    self.log && self.log('performance: blockingDiv %s started with pending blockingDiv requests: %s', blockingDivDescription, self.pendingBlockingDivRequests);
                }
                self.pendingBlockingDivRequests++;
            },

            /**
             * @memberof $dyn.ui.PerformanceTimer
             * @function _stopBlockingDiv
             * @internal
             * @summary Log the end of a blocking div
             */
            _stopBlockingDiv: function () {
                var self = $dyn.ui.PerformanceTimer;

                self.pendingBlockingDivRequests = 0;

                self.blockingDivEnd = performance.now();
                var blockingTime = self.blockingDivEnd - self.blockingDivStart;
                self.blockingDivTime += blockingTime;

                self.log && self.log('performance: blockingDiv completed: %s', self.blockingDivTime);
            },

            _update: function () {
                var self = $dyn.ui.PerformanceTimer;

                var now = performance.now();
                var time = now - self.startTime;
                var isTrivial = time < self.trivialTime;

                var currentClient = now - self.ajaxEnd;
                var totalServer = self.ajaxTime();
                var blockingDivTime = self.blockingDivTime;
                var blockingDivDescription = self.blockingDivDescription || 'BlockingDiv';
                var totalClient = time - totalServer;
                
                var action = self.action || $dyn.label('PerformanceTimer_ClientAction');
                var timing = {
                    Duration: time,
                    StartTime: self.startTime,
                    Counters: [
                        {
                            Name: 'PerformanceTimer_TotalClient',
                            Value: totalClient,
                        },
                        {
                            Name: 'PerformanceTimer_TotalServer',
                            Value: totalServer,
                        }
                    ],
                    SimpleAttributes: [
                         {
                             Name: 'PerformanceTimer_Action',
                             Value: action,
                         }
                    ],
                    ajaxTime: totalServer,
                    currentClient: currentClient,
                    totalClient: totalClient,
                    size: self.bandwidth(),
                    Name: action,
                    ShortName: action,
                    Label: 'PerformanceTimer_TotalTime',
                    handlerTimings: self.handlerTimings,
                    Trivial: isTrivial,
                    Steps: self.segments,
                };

                self.segments = [];

                if (!isTrivial || totalServer > 0) {
                    self.timings().unshift(timing);
                    if (self.timings().length > 25) {
                        self.timings().length = 25;
                    }

                    self.oppTiming(timing);
                    self.timings.valueChanged.raise();
                    self.log && self.log(
                        'performance: overall: %s, ajax: %s, current client: %s, overall client: %s, size: %s, action: %s, browser session activity ID: %s, browser interation activity ID: %s',
                        timing.Duration,
                        timing.ajaxTime,
                        timing.currentClient,
                        timing.totalClient,
                        timing.size,
                        timing.Name,
                        $dyn.browserActivity.BrowserSessionActivity.Id,
                        $dyn.browserActivity.BrowserInteractionActivity.Id);

                    if ($dyn.telemetry) {
                        var preClient = totalClient - currentClient;
                        $dyn.telemetry.PerformanceTimer(preClient, totalServer, currentClient, action, self.ajaxActivityId, blockingDivTime, blockingDivDescription);
                    }

                    self.duration(time);

                    self.updateExpected && self.updateExpected();
                }
            },
            _clientBusy: function () {
                var self = $dyn.ui.PerformanceTimer;
                if ($dyn.boot.booting) {
                    return;
                }
                if ($dyn.clientBusy()) {
                    self.log && self.log('performance: client busy - begin tracking time');
                    $dyn.time('busy');

                    self.inClient = true;
                    $dyn.time('client');

                    self.startTime = self.clientStart = self.ajaxEnd = performance.now();
                    self.ajaxTime(0);
                    self.bandwidth(0);
                    self.actionSet = {};
                    self.action = '';
                    self.lastAjaxSegment = null;
                    self.openedForm = false;
                    self.handlerTimings = {};
                    self.ajaxActivityId = '';
                    self.blockingDivTime = 0;
                    self.blockingDivDescription = '';
                } else {
                    if (self.inClient) {
                        self.clientEnd = performance.now();
                        self.segments.push({
                            Name: $dyn.label('PerformanceTimer_ClientAction'),
                            Duration: self.clientEnd - self.clientStart,
                            StartTime: self.clientStart,
                            Type: self.clientType
                        });
                        $dyn.timeEnd('client');
                        self.inClient = false;
                    }
                    self.log && self.log('performance: client not busy - ending tracking times');
                    self._update();
                    self.actions(Object.keys(self.actionSet));
                    $dyn.timeEnd('busy');
                }
            },
            _addNavBarButton: function () { },

            _addAction: function (action) {
                /// <summary>Adds an action to the list of actions. Can be a server interaction.</summary>
                var self = $dyn.ui.PerformanceTimer;
                if (!self.action) {
                    self.action = action;
                } else {
                    self.action += " " + action;
                }
            },

            _getTimings: function () {
                // gets and clears the timings
                var self = $dyn.ui.PerformanceTimer;

                var timings = self.timings;
                self.timings = [];

                return timings;
            },
            _logLastHandlerTimings: function () {
                var self = $dyn.ui.PerformanceTimer;
                if (console.table) {
                    console.table(self.timings[self.timings.length - 1].handlerTimings);
                }
                else {
                    self.log && self.log(self.timings[self.timings.length - 1].handlerTimings);
                }
            }
        };

        $dyn.clientBusy.valueChanged.subscribe($dyn.ui.PerformanceTimer._clientBusy);

        $(document).on("ajaxSuccess", function ajaxSuccess(event, request, settings) {
            $dyn.ui.PerformanceTimer._ajaxResponse(request);
        });
    }
})();